import React from "react";
import { Context } from "./context";

function useControls() {
  const ctx = React.useContext(Context);
  const hasOpenedItems = Object.values(ctx.mappedEl).find(
    ({ isOpen }) => isOpen,
  );
  const canOpen = !Object.values(ctx.mappedEl).every(({ isOpen }) => isOpen);
  const deepestOpenedLevel = ctx.deepestOpenedLevel;

  return {
    canCollapse: hasOpenedItems,
    canOpen,
    collapseAll: () =>
      ctx.setMappedEl(
        Object.entries(ctx.mappedEl).reduce(
          (mappedEl, [key, state]) => ({
            ...mappedEl,
            [key]: { ...state, isOpen: false },
          }),
          {},
        ),
      ),
    collapseLast: () =>
      ctx.setMappedEl(
        Object.entries(ctx.mappedEl).reduce(
          (mappedEl, [key, state]) => ({
            ...mappedEl,
            [key]: { ...state, isOpen: state.depth < deepestOpenedLevel },
          }),
          {},
        ),
      ),
    extendAll: () =>
      ctx.setMappedEl(
        Object.entries(ctx.mappedEl).reduce(
          (mappedEl, [key, state]) => ({
            ...mappedEl,
            [key]: { ...state, isOpen: true },
          }),
          {},
        ),
      ),
    extendLast: () => {
      const hasOpenedListBeyondFirstDepth = Object.values(ctx.mappedEl).find(
        ({ isOpen, depth }) => isOpen && depth >= 0,
      );

      ctx.setMappedEl(
        Object.entries(ctx.mappedEl).reduce(
          (mappedEl, [key, state]) => ({
            ...mappedEl,
            [key]: {
              ...state,
              isOpen:
                state.depth <=
                deepestOpenedLevel + (hasOpenedListBeyondFirstDepth ? 1 : 0),
            },
          }),
          {},
        ),
      );
    },
  };
}

function useToggle(key: string, depth: number, isOpenByDefault?: boolean) {
  const ctx = React.useContext(Context);
  const isInitialized = typeof ctx.mappedEl[key] !== "undefined";
  const isOpen = !isInitialized
    ? isOpenByDefault
    : key in ctx.mappedEl
      ? ctx.mappedEl[key].isOpen
      : undefined;

  React.useEffect(() => {
    !isInitialized &&
      ctx.initialize(key, depth, typeof isOpen !== "undefined" ? isOpen : true);
  }, [isInitialized]);

  return {
    isOpen,
    toggle: () => (isOpen ? ctx.close(key, depth) : ctx.open(key, depth)),
  };
}

function useUtils() {
  const ctx = React.useContext(Context);

  return {
    mappedEl: ctx.mappedEl,
    isOpen: (key: string) => ctx.mappedEl[key] && ctx.mappedEl[key].isOpen,
  };
}

export { useToggle, useControls, useUtils };
